import { STORE_VALIDATE_ACCOUNT_MISMATCH } from '@cf-shared/store-validate-errors';

/** Returns true when an IAP `restorePurchasesAsync` error code is an
 *  expected, benign outcome from a passive auto-restore probe on the
 *  subscribe page — i.e. one we should silently fall through on
 *  instead of reporting to Sentry.
 *
 *  Lives in a separate Angular-free module so the test file can import
 *  it without bootstrapping the full subscribe page component (mirrors
 *  the [subscribe-frame-message-filter.ts](./subscribe-frame-message-filter.ts) pattern).
 *
 *  Expected outcomes:
 *  - `'already_linked'` — the store subscription is bound to another
 *    account. Showing an alert here would loop the fresh-signup user
 *    back onto the subscribe page (see the long comment at the call
 *    site in subscribe-native-rail.ts, in checkExistingStorePurchase,
 *    for the full loop trace).
 *  - `STORE_VALIDATE_ACCOUNT_MISMATCH` — the store says a different one of
 *    our accounts started the purchase (#5384). The same verdict as
 *    `already_linked`, for an owning account that no longer exists, and
 *    just as expected on a passive probe.
 *  - `'No existing purchases found.'` — the user has no Apple sub on
 *    the device. Normal for a fresh device.
 *  - `'Subscription is not active (state: SUBSCRIPTION_STATE_*).'` —
 *    Google Play returns this whenever the user's previous sub is in a
 *    non-Active terminal state. We've seen `_CANCELED` (Sentry
 *    CYBERNETIC-FITNESS-APP-43F, 19 events / 5 users) but the same
 *    pattern surfaces for `_EXPIRED`, `_REVOKED`, `_PAUSED`. Matching on
 *    the `SUBSCRIPTION_STATE_` prefix means future Play-side states
 *    don't reintroduce the noise.
 *
 *  Any other code — Store transport failure, JWS decode error, etc. —
 *  is unexpected and the caller should surface to Sentry via
 *  `trackSilentProblem`. */
export function isExpectedRestoreError(code: string | undefined): boolean {
  if (!code) return false;
  if (code === 'already_linked') return true;
  if (code === STORE_VALIDATE_ACCOUNT_MISMATCH) return true;
  if (code === 'No existing purchases found.') return true;
  if (code.startsWith('Subscription is not active (state: SUBSCRIPTION_STATE_')) return true;
  return false;
}

/** The part of a restore result that decides whether the auto-restore probe
 *  reports it. Structural rather than `IAPPurchaseResult` itself, so this file
 *  stays free of the Angular service it would otherwise import. */
export interface AutoRestoreOutcome {
  error?: string;
  alreadyReported?: boolean;
}

/** Returns true when the subscribe page's passive auto-restore should report
 *  this failed restore to Sentry as `SubscribeAutoRestoreError`.
 *
 *  A failure that came from a throw was already reported by
 *  `IAPService.restorePurchasesAsync`, with its name, stack and a network tag,
 *  so reporting it again would only file the same failure twice without that
 *  detail (df-d2afc9172013). Every other unexpected code is a store answer and
 *  stays loud on purpose (design-docs/iap-subscriptions.md). */
export function shouldReportAutoRestoreError(result: AutoRestoreOutcome): boolean {
  if (!result.error) return false;
  if (result.alreadyReported) return false;
  return !isExpectedRestoreError(result.error);
}
